import { useCallback, useEffect, useRef } from "react";
import type { DialogProps } from "@mui/material";
import { dialogCancelOnClose } from "@/helper/dialogCancelOnClose";

type EscapeCloseEntry = {
  generation: number;
  close: () => void;
};

const escapeCloseHandlers: EscapeCloseEntry[] = [];
let isListening = false;
let nextGeneration = 0;

const isEscapeKey = (event: KeyboardEvent) =>
  event.key === "Escape" || event.code === "Escape" || event.keyCode === 27;

const getTopHandler = (): EscapeCloseEntry | undefined => {
  if (escapeCloseHandlers.length === 0) {
    return undefined;
  }

  return escapeCloseHandlers.reduce((top, entry) =>
    entry.generation >= top.generation ? entry : top,
  );
};

const handleGlobalEscapeKeyDown = (event: KeyboardEvent) => {
  if (!isEscapeKey(event)) {
    return;
  }

  const topHandler = getTopHandler();
  if (!topHandler) {
    return;
  }

  event.preventDefault();
  topHandler.close();
};

const ensureEscapeListener = () => {
  if (!isListening) {
    document.addEventListener("keydown", handleGlobalEscapeKeyDown);
    isListening = true;
  }
};

const cleanupEscapeListenerIfIdle = () => {
  if (escapeCloseHandlers.length === 0 && isListening) {
    document.removeEventListener("keydown", handleGlobalEscapeKeyDown);
    isListening = false;
  }
};

const useEscapeToClose = (
  open: boolean,
  onClose?: (() => void) | null,
  enabled: boolean = true,
) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const generationRef = useRef<number | null>(null);
  const hasCloseHandler = typeof onClose === "function";

  useEffect(() => {
    if (!open) {
      generationRef.current = null;
      return;
    }

    if (!enabled || !hasCloseHandler) {
      return;
    }

    if (generationRef.current === null) {
      nextGeneration += 1;
      generationRef.current = nextGeneration;
    }

    const entry: EscapeCloseEntry = {
      generation: generationRef.current,
      close: () => onCloseRef.current?.(),
    };

    const existingIndex = escapeCloseHandlers.findIndex(
      (handler) => handler.generation === entry.generation,
    );
    if (existingIndex !== -1) {
      escapeCloseHandlers.splice(existingIndex, 1);
    }

    escapeCloseHandlers.push(entry);
    ensureEscapeListener();

    return () => {
      const index = escapeCloseHandlers.indexOf(entry);
      if (index !== -1) {
        escapeCloseHandlers.splice(index, 1);
      }
      cleanupEscapeListenerIfIdle();
    };
  }, [open, enabled, hasCloseHandler]);
};

export type UseModalDismissOptions = {
  enabled?: boolean;
  allowBackdropClick?: boolean;
};

export type ModalDismissProps = {
  onClose: NonNullable<DialogProps["onClose"]>;
};

/**
 * Single entry point for “dismiss this modal like Cancel”.
 *
 * - Always registers focus-independent Escape (nested-safe).
 * - Returns Dialog `onClose` props you can spread; Escape-only callers may ignore the return.
 *
 * @example Dialog
 * const dismiss = useModalDismiss(open, onClose);
 * <Dialog open={open} {...dismiss} />
 *
 * @example MUI Modal with custom onClose
 * useModalDismiss(open, onClose);
 * <Modal open={open} onClose={...} />
 */
export const useModalDismiss = (
  open: boolean,
  onCancel?: (() => void) | null,
  options?: UseModalDismissOptions,
): ModalDismissProps => {
  const enabled = options?.enabled ?? true;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const dismiss = useCallback(() => {
    if (!enabled) {
      return;
    }
    onCancelRef.current?.();
  }, [enabled]);

  useEscapeToClose(open, dismiss, enabled && typeof onCancel === "function");

  return {
    onClose: dialogCancelOnClose(dismiss, options),
  };
};
